import { useQuery } from "@tanstack/react-query";
import { mfgCatalogApi, type MfgItemDetail, type MfgItemHierarchyEdge } from "@/lib/mfg-catalog";

export interface MfgTreeNode { item: MfgItemDetail; edges: { edge: MfgItemHierarchyEdge; child: MfgTreeNode }[]; }

// Bounded recursive walk — Allmark trees are shallow. Loads each node once.
async function loadNode(id: number, seen: Set<number>): Promise<MfgTreeNode> {
  const item = await mfgCatalogApi.get(id);
  const edges: MfgTreeNode["edges"] = [];
  for (const edge of item.children) {
    if (seen.has(edge.childItemId)) continue; // guard DAG re-entry
    seen.add(edge.childItemId);
    edges.push({ edge, child: await loadNode(edge.childItemId, seen) });
  }
  return { item, edges };
}

export function useMfgTree(rootId: number) {
  return useQuery({
    queryKey: ["mfg-tree", rootId],
    queryFn: () => loadNode(rootId, new Set([rootId])),
    enabled: rootId > 0,
  });
}

/** Collect the Variant groups, Alternative groups, and Material leaves from a loaded tree. */
export function collectGroups(root: MfgTreeNode) {
  const variants: { node: MfgTreeNode; options: MfgTreeNode[] }[] = [];
  const alternatives: { node: MfgTreeNode; options: MfgTreeNode[] }[] = [];
  const materials: MfgTreeNode[] = [];
  const walk = (n: MfgTreeNode) => {
    if (n.item.itemTypeCode === "Variant") variants.push({ node: n, options: n.edges.map((e) => e.child) });
    else if (n.item.itemTypeCode === "Alternative") alternatives.push({ node: n, options: n.edges.map((e) => e.child) });
    else if (n.item.itemTypeCode === "Material") materials.push(n);
    n.edges.forEach((e) => walk(e.child));
  };
  walk(root);
  return { variants, alternatives, materials };
}
